import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, TrendingUp, Info, CheckCircle2, AlertCircle, User, Sparkles, Copy, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SalaryRangeChart } from "./SalaryRangeChart";
import { z } from "zod";
import { useSalaryProfile } from "@/hooks/useSalaryProfile";
import { useSaveSalaryAnalysis } from "@/hooks/useSaveSalaryAnalysis";
import { useSaveNegotiationScenario } from "@/hooks/useSaveNegotiationScenario";

const analysisSchema = z.object({
  jobTitle: z.string().min(2, "Job title must be at least 2 characters").max(100),
  location: z.string().min(2, "Location is required").max(100),
  experience: z.number().min(0).max(50),
  industry: z.string().optional(),
  companySize: z.string().optional(),
  education: z.string().optional(),
  currentSalary: z.number().optional(),
});

interface MarketData {
  average: number;
  percentiles: {
    p10: number;
    p25: number;
    p50: number;
    p75: number;
    p90: number;
  };
  sources: string[];
  confidence: string;
  metadata?: {
    estimationMethod: string;
    lastUpdated: string;
  };
}

export function MarketAnalysis() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [strategy, setStrategy] = useState("");
  const [copied, setCopied] = useState(false);
  const { data: profileData } = useSalaryProfile();
  const saveAnalysis = useSaveSalaryAnalysis();
  const saveScenario = useSaveNegotiationScenario();
  
  // Form state
  const [jobTitle, setJobTitle] = useState("");
  const [location, setLocation] = useState("");
  const [experience, setExperience] = useState([5]);
  const [industry, setIndustry] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [education, setEducation] = useState("");
  const [currentSalary, setCurrentSalary] = useState("");

  // Auto-fill from profile when loaded
  useEffect(() => {
    if (profileData && !jobTitle) {
      setJobTitle(profileData.jobTitle);
      setLocation(profileData.location);
      setExperience([profileData.yearsExperience]);
      setEducation(profileData.educationLevel);
    }
  }, [profileData]);

  const handleAnalyze = async () => {
    try {
      // Validate input
      const validated = analysisSchema.parse({
        jobTitle,
        location,
        experience: experience[0],
        industry: industry || undefined,
        companySize: companySize || undefined,
        education: education || undefined,
        currentSalary: currentSalary ? parseInt(currentSalary) : undefined,
      });

      setLoading(true);

      // Generate context hash for idempotency
      const encoder = new TextEncoder();
      const contextData = `${validated.jobTitle}:${validated.location}:${validated.experience}`;
      const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(contextData));
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const contextHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
      const refId = `salary:market:${contextHash}:${Date.now()}`;

      // Phase 1: Charge credits atomically
      const { data: creditResult, error: creditError } = await supabase.functions.invoke('credits-spend', {
        body: { featureKey: 'salary.market_analysis', refId }
      });

      if (creditError) {
        const errorData = creditError as any;
        if (errorData.error === 'INSUFFICIENT_CREDITS') {
          toast.error(errorData.message || 'Insufficient credits');
          return;
        }
        throw creditError;
      }

      // Phase 2: Call salary market data edge function
      const { data, error } = await supabase.functions.invoke('salary-market-data', {
        body: {
          jobTitle: validated.jobTitle,
          location: validated.location,
          experience: validated.experience,
          industry: validated.industry,
          companySize: validated.companySize,
          education: validated.education,
        },
      });

      if (error) throw error;

      setMarketData(data);
      setStep(2);
      
      // Auto-save to database
      saveAnalysis.mutate({
        jobTitle: validated.jobTitle,
        location: validated.location,
        yearsExperience: validated.experience,
        skills: profileData?.skills || [],
        currentSalary: validated.currentSalary,
        industry: validated.industry,
        companySize: validated.companySize,
        educationLevel: validated.education,
        marketData: data,
      });
      
      if (!creditResult.idempotent && creditResult.cost > 0) {
        toast.success(`Market analysis complete! Used ${creditResult.cost} credits. ${creditResult.remaining} remaining.`);
      } else {
        toast.success("Market analysis complete!");
      }

      // Refresh credits display
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        console.error("[SALARY-MARKET] Error:", error);
        toast.error("Failed to fetch salary data. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setMarketData(null);
    setStrategy("");
  };

  const handleGenerateStrategy = async () => {
    if (!jobTitle || !marketData) return;

    setStrategyLoading(true);
    setStrategy("");

    try {
      const targetSalary = currentSalary 
        ? Math.max(parseInt(currentSalary), marketData.percentiles.p75)
        : marketData.percentiles.p75;

      // Generate context hash for idempotency
      const encoder = new TextEncoder();
      const contextData = `${jobTitle}:${targetSalary}:${experience[0]}`;
      const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(contextData));
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const contextHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
      const refId = `salary:negotiation:${contextHash}:${Date.now()}`;

      // Phase 1: Charge credits atomically
      const { data: creditResult, error: creditError } = await supabase.functions.invoke('credits-spend', {
        body: { featureKey: 'salary.negotiation_strategy', refId }
      });

      if (creditError) {
        const errorData = creditError as any;
        if (errorData.error === 'INSUFFICIENT_CREDITS') {
          toast.error(errorData.message || 'Insufficient credits');
          return;
        }
        throw creditError;
      }

      // Phase 2: Generate strategy
      const { data, error } = await supabase.functions.invoke('generate-negotiation-strategy', {
        body: {
          jobTitle,
          yearsExperience: experience[0],
          currentSalary: currentSalary ? parseFloat(currentSalary) : null,
          targetSalary,
          scenario: currentSalary ? 'raise_request' : 'initial_offer',
        },
      });

      if (error) throw error;

      setStrategy(data.strategy);
      
      saveScenario.mutate({
        offerAmount: currentSalary ? parseFloat(currentSalary) : 0,
        counterOffer: targetSalary,
        negotiationStrategy: data.strategy,
      });

      if (!creditResult.idempotent && creditResult.cost > 0) {
        toast.success(`Strategy generated! Used ${creditResult.cost} credits. ${creditResult.remaining} remaining.`);
      } else {
        toast.success("Strategy generated!");
      }

      // Refresh credits display
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    } catch (error: any) {
      console.error('[SALARY-STRATEGY] Error:', error);
      toast.error(error.message || "Failed to generate strategy");
    } finally {
      setStrategyLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(strategy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard!");
  };

  if (step === 2 && marketData) {
    const currentSalaryNum = currentSalary ? parseInt(currentSalary) : null;
    const marketAverage = marketData.average;
    const difference = currentSalaryNum ? currentSalaryNum - marketAverage : null;
    const percentDiff = currentSalaryNum && difference !== null 
      ? ((difference / marketAverage) * 100).toFixed(1) 
      : null;

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-3xl">Your Market Value</CardTitle>
                <CardDescription>Based on {marketData.sources.join(", ")}</CardDescription>
              </div>
              <Button variant="outline" onClick={handleReset}>
                New Analysis
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Main Value Display */}
            <div className="text-center py-8 bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg">
              <div className="text-sm text-muted-foreground mb-2">Market Average</div>
              <div className="text-5xl font-bold mb-2">
                ${marketAverage.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">
                Range: ${marketData.percentiles.p25.toLocaleString()} - ${marketData.percentiles.p75.toLocaleString()}
              </div>
              <Badge 
                variant={marketData.confidence === "high" ? "default" : "secondary"}
                className="mt-4"
              >
                {marketData.confidence === "high" ? "High Confidence" : "Industry Estimate"}
              </Badge>
            </div>

            {/* Comparison if current salary provided */}
            {currentSalaryNum && difference !== null && percentDiff !== null && (
              <Card className={difference >= 0 ? "border-green-500 bg-green-50 dark:bg-green-950" : "border-orange-500 bg-orange-50 dark:bg-orange-950"}>
                <CardContent className="pt-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    {difference >= 0 ? (
                      <CheckCircle2 className="h-8 w-8 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-8 w-8 text-orange-600 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold break-words">
                        {difference >= 0 
                          ? `You're earning ${percentDiff}% above market average`
                          : `You're earning ${Math.abs(parseFloat(percentDiff))}% below market average`
                        }
                      </div>
                      <div className="text-sm text-muted-foreground break-words">
                        Your salary: ${currentSalaryNum.toLocaleString()} | 
                        Market average: ${marketAverage.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Salary Range Chart */}
            <SalaryRangeChart 
              marketData={marketData}
              currentSalary={currentSalaryNum}
              jobTitle={jobTitle}
            />

            {/* Percentile Breakdown */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { label: "10th", value: marketData.percentiles.p10 },
                { label: "25th", value: marketData.percentiles.p25 },
                { label: "50th", value: marketData.percentiles.p50 },
                { label: "75th", value: marketData.percentiles.p75 },
                { label: "90th", value: marketData.percentiles.p90 },
              ].map((item) => (
                <Card key={item.label}>
                  <CardContent className="pt-6 text-center">
                    <div className="text-xs text-muted-foreground mb-1">{item.label} Percentile</div>
                    <div className="text-xl font-bold">${(item.value / 1000).toFixed(0)}k</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Analysis Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  About This Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Job Title:</span>
                  <span className="font-medium">{jobTitle}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Location:</span>
                  <span className="font-medium">{location}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Experience:</span>
                  <span className="font-medium">{experience[0]} years</span>
                </div>
                {industry && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Industry:</span>
                    <span className="font-medium">{industry}</span>
                  </div>
                )}
                {marketData.metadata && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Data Method:</span>
                    <span className="font-medium capitalize">
                      {marketData.metadata.estimationMethod.replace(/_/g, " ")}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Action Items */}
            <div className="flex gap-4">
              <Button 
                className="flex-1" 
                size="lg"
                onClick={handleGenerateStrategy}
                disabled={strategyLoading}
              >
                {strategyLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Generating Strategy...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Generate Negotiation Strategy
                  </>
                )}
              </Button>
            </div>

            {/* Strategy Display */}
            {strategy && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Your Personalized Strategy</CardTitle>
                    <Button variant="outline" size="sm" onClick={handleCopy}>
                      {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={strategy}
                    readOnly
                    className="min-h-[400px] font-mono text-sm"
                  />
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Analyze Your Market Value</CardTitle>
            <CardDescription>
              Get data-driven salary insights based on your experience and location
            </CardDescription>
          </div>
          {profileData && (
            <Badge variant="secondary" className="gap-2">
              <User className="h-3 w-3" />
              Auto-filled from profile
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="jobTitle">Job Title *</Label>
            <Input
              id="jobTitle"
              placeholder="e.g., Software Engineer"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location *</Label>
            <Input
              id="location"
              placeholder="e.g., San Francisco, CA"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="experience">
              Years of Experience: {experience[0]} years
            </Label>
            <Slider
              id="experience"
              min={0}
              max={30}
              step={1}
              value={experience}
              onValueChange={setExperience}
              className="py-4"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="industry">Industry</Label>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger id="industry">
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="technology">Technology</SelectItem>
                <SelectItem value="finance">Finance</SelectItem>
                <SelectItem value="healthcare">Healthcare</SelectItem>
                <SelectItem value="education">Education</SelectItem>
                <SelectItem value="retail">Retail</SelectItem>
                <SelectItem value="manufacturing">Manufacturing</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="companySize">Company Size</Label>
            <Select value={companySize} onValueChange={setCompanySize}>
              <SelectTrigger id="companySize">
                <SelectValue placeholder="Select company size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="startup">Startup (1-50)</SelectItem>
                <SelectItem value="small">Small (51-200)</SelectItem>
                <SelectItem value="mid">Mid-size (201-1000)</SelectItem>
                <SelectItem value="large">Large (1001-5000)</SelectItem>
                <SelectItem value="enterprise">Enterprise (5000+)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="education">Education Level</Label>
            <Select value={education} onValueChange={setEducation}>
              <SelectTrigger id="education">
                <SelectValue placeholder="Select education" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high-school">High School</SelectItem>
                <SelectItem value="associate">Associate Degree</SelectItem>
                <SelectItem value="bachelors">Bachelor's Degree</SelectItem>
                <SelectItem value="masters">Master's Degree</SelectItem>
                <SelectItem value="phd">Ph.D.</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="currentSalary">
              Current Salary (Optional)
              <span className="text-xs text-muted-foreground ml-2">
                We'll show how you compare to market
              </span>
            </Label>
            <Input
              id="currentSalary"
              type="number"
              placeholder="e.g., 85000"
              value={currentSalary}
              onChange={(e) => setCurrentSalary(e.target.value)}
              min={0}
            />
          </div>
        </div>

        <Button 
          onClick={handleAnalyze} 
          disabled={loading || !jobTitle || !location}
          className="w-full"
          size="lg"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Analyzing Market Data...
            </>
          ) : (
            <>
              <TrendingUp className="mr-2 h-5 w-5" />
              Analyze Market Value
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
